import { Injectable } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import { BehaviorSubject, Observable } from 'rxjs';
import SockJS from 'sockjs-client';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/services/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private client: Client;
  private messageSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private connectedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private onlineUsersSubject: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private messageQueue: { senderId: number; receiverId: number; content: string }[] = [];
  private connectionPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;
  private isReconnecting = false;

  constructor(private authService: AuthenticationService) {
    this.client = new Client();
  }

  private initializeWebSocket(): void {
    console.log('Initializing WebSocket with SockJS');
    let authToken = this.authService.getToken() || '';
    console.log('Raw JWT Token:', authToken);

    if (authToken.startsWith('Bearer ')) {
      authToken = authToken.replace('Bearer ', '');
    }

    if (!authToken) {
      console.error('No valid JWT token found. Cannot establish WebSocket connection.');
      this.connectedSubject.next(false);
      this.handleReconnect();
      return;
    }

    this.client = new Client({
      webSocketFactory: () => {
        console.log('Connecting to:', `${environment.BasePath}/ws`);
        return new SockJS(`${environment.BasePath}/ws`);
      },
      connectHeaders: {
        Authorization: `Bearer ${authToken}`,
      },
      reconnectDelay: 0,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str) => {
        console.log('STOMP: ' + str);
      },
      onConnect: (frame) => {
        console.log('WebSocket connected:', frame);
        this.connectedSubject.next(true);
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.isReconnecting = false;
        this.subscribeToMessages();
        this.subscribeToOnlineUsers();
        this.flushMessageQueue();
        if (this.connectionPromise) {
          this.connectionPromise = Promise.resolve();
        }
      },
      onDisconnect: () => {
        console.log('WebSocket disconnected');
        this.connectedSubject.next(false);
        this.onlineUsersSubject.next([]);
        this.connectionPromise = null;
        this.handleReconnect();
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        this.connectedSubject.next(false);
        this.onlineUsersSubject.next([]);
        this.connectionPromise = null;
        const errorMessage = frame.headers?.message?.includes('Authentication')
        this.messageSubject.next({ error: errorMessage });
        this.handleReconnect();
      },
      onWebSocketClose: (event) => {
        console.error('WebSocket closed:', event);
        this.connectedSubject.next(false);
        this.onlineUsersSubject.next([]);
        this.connectionPromise = null;
        this.handleReconnect();
      },
      onWebSocketError: (error) => {
        console.error('WebSocket error:', error);
        this.connectedSubject.next(false);
        this.onlineUsersSubject.next([]);
        this.messageSubject.next({ error: 'WebSocket connection error. Please try again.' });
        this.handleReconnect();
      },
    });
  }

  private handleReconnect(): void {
    if (this.isReconnecting) {
      console.log('Reconnection already in progress. Skipping.');
      return;
    }

    const authToken = this.authService.getToken() || '';
    if (!authToken) {
      console.error('No valid JWT token available. Redirecting to login.');
      this.connectedSubject.next(false);
      this.isReconnecting = false;
      this.connectionPromise = null;
      this.authService.logOut();
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached. Retrying after delay.');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 30000;
    }

    this.reconnectAttempts++;
    this.isReconnecting = true;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectDelay}ms...`);

    setTimeout(() => {
      this.connect();
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
    }, this.reconnectDelay);
  }

  connect(): void {
    if (this.client?.connected) {
      console.log('WebSocket already connected');
      return;
    }

    this.connectionPromise = new Promise<void>((resolve, reject) => {
      console.log('Initializing WebSocket client');
      this.initializeWebSocket();
      try {
        this.client.activate();
        resolve();
      } catch (error) {
        console.error('Failed to activate WebSocket client:', error);
        reject(error);
        this.handleReconnect();
      }
    }).catch((error) => {
      console.error('Connection promise rejected:', error);
      this.handleReconnect();
    });
  }

  private subscribeToMessages(): void {
    console.log('Subscribing to /user/queue/messages');
    this.client.subscribe(`/user/queue/messages`, (message: IMessage) => {
      try {
        const parsedMessage = JSON.parse(message.body);
        console.log('Received message:', parsedMessage);
        if (!parsedMessage.id || (!parsedMessage.sender && !parsedMessage.senderName)) {
          console.warn('Invalid message received:', parsedMessage);
          return;
        }
        this.messageSubject.next(parsedMessage);
      } catch (error) {
        console.error('Failed to parse message:', message.body, error);
      }
    });

    console.log('Subscribing to /topic/broadcast');
    this.client.subscribe(`/topic/broadcast`, (message: IMessage) => {
      try {
        const parsedMessage = JSON.parse(message.body);
        console.log('Received broadcast:', parsedMessage);
        if (!parsedMessage.senderName) {
          console.warn('Invalid broadcast message received:', parsedMessage);
          return;
        }
        this.messageSubject.next(parsedMessage);
      } catch (error) {
        console.error('Failed to parse broadcast:', message.body, error);
      }
    });
  }

  private subscribeToOnlineUsers(): void {
    console.log('Subscribing to /topic/online-users');
    this.client.subscribe(`/topic/online-users`, (message: IMessage) => {
      try {
        const onlineUsers = JSON.parse(message.body);
        console.log('Received online users:', onlineUsers);
        this.onlineUsersSubject.next(onlineUsers);
      } catch (error) {
        console.error('Failed to parse online users:', message.body, error);
        this.onlineUsersSubject.next([]);
      }
    });
  }

  private flushMessageQueue(): void {
    console.log('Flushing message queue, connected:', this.client.connected);
    while (this.messageQueue.length > 0 && this.client.connected) {
      const { senderId, receiverId, content } = this.messageQueue.shift()!;
      if (!senderId || (receiverId !== 0 && !receiverId) || !content) {
        console.warn('Skipping invalid queued message:', { senderId, receiverId, content });
        continue;
      }
      this.sendMessageInternal(senderId, receiverId, content);
    }
  }

  private sendMessageInternal(senderId: number, receiverId: number, content: string): void {
    if (!this.client.connected) {
      console.warn('Cannot send message: WebSocket is not connected');
      this.messageQueue.push({ senderId, receiverId, content });
      return;
    }
    if (!Number.isInteger(senderId) || (receiverId !== 0 && !Number.isInteger(receiverId))) {
      console.error('Invalid senderId or receiverId: must be integers', { senderId, receiverId });
      this.messageSubject.next({ error: 'Invalid user IDs' });
      return;
    }
    try {
      console.log('Sending message:', { senderId, receiverId, content });
      this.client.publish({
        destination: '/app/chat/send',
        body: JSON.stringify({
          senderId,
          receiverId,
          content,
        }),
      });
    } catch (error) {
      console.error('Failed to publish message:', error);
      this.messageQueue.push({ senderId, receiverId, content });
    }
  }

  isConnected(): Observable<boolean> {
    return this.connectedSubject.asObservable();
  }

  getOnlineUsers(): Observable<number[]> {
    return this.onlineUsersSubject.asObservable();
  }

  async subscribeToUser(userId: number): Promise<Observable<any>> {
    console.log('Waiting for WebSocket connection for user:', userId);
    await this.connectionPromise;
    console.log('Subscribed to user:', userId);
    return this.messageSubject.asObservable();
  }

  async sendMessage(senderId: number, receiverId: number, content: string): Promise<void> {
    console.log('Attempting to send message, connected:', this.client.connected);
    if (!this.client.connected) {
      console.warn('WebSocket is not connected. Queuing message.');
      this.messageQueue.push({ senderId, receiverId, content });
      await this.connectionPromise;
      this.flushMessageQueue();
    } else {
      this.sendMessageInternal(senderId, receiverId, content);
    }
  }

  async sendBroadcast(adminId: number, content: string): Promise<void> {
    console.log('Attempting to send broadcast, connected:', this.client.connected);
    if (!this.client.connected) {
      console.warn('WebSocket is not connected. Queuing broadcast.');
      this.messageQueue.push({ senderId: adminId, receiverId: 0, content });
      await this.connectionPromise;
      this.flushMessageQueue();
    } else {
      if (!Number.isInteger(adminId)) {
        console.error('Invalid adminId: must be an integer', { adminId });
        this.messageSubject.next({ error: 'Invalid admin ID' });
        return;
      }
      try {
        console.log('Sending broadcast:', { adminId, content });
        this.client.publish({
          destination: '/app/chat/broadcast',
          body: JSON.stringify({
            adminId,
            content,
          }),
        });
      } catch (error) {
        console.error('Failed to publish broadcast:', error);
        this.messageQueue.push({ senderId: adminId, receiverId: 0, content });
      }
    }
  }

  disconnect(): void {
    console.log('Disconnecting WebSocket');
    if (this.client) {
      this.client.deactivate();
      this.connectedSubject.next(false);
      this.onlineUsersSubject.next([]);
      this.connectionPromise = null;
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
    }
  }

  reconnectWithNewCredentials(): void {
    console.log('Reinitializing WebSocket with new credentials');
    this.disconnect();
    this.connect();
  }
}
